import {
  EventLogItem,
  EventLogData,
  TransformedEventLogsResponse,
  NewTelemetryItem,
  NewTelemetryData,
  TelemetryValue,
} from './kavachApiTypes';
import {
  KavachSwitch,
  KavachAlert,
  KavachStats,
  StationStats,
  PortStatus,
  Port,
  AssetDetails,
  KavachDeviceAssetInfo,
} from './kavachTypes';

/**
 * Extract station code from device name
 * Device names follow patterns like:
 * - "NCR13_PRYJ_FZD_FZD-UP-LINE" → Station: "FZD" (3rd segment after underscore)
 * - "NCR14_PRYJ_FZD_FZD-LSC-54-MNR-UP-LINE" → Station: "FZD"
 * - "MNR-LSC-56-SKB-192.168.11.38" → Station: "MNR"
 * - "SKB-192.168.11.39" → Station: "SKB"
 */
export const extractStationFromName = (deviceName: string): string => {
  if (!deviceName) return 'UNKNOWN';

  // Handle underscore-separated format: NCR13_PRYJ_FZD_FZD-UP-LINE
  // Format: NCRxx_DIVISION_STATION_...
  if (deviceName.includes('_')) {
    const underscoreParts = deviceName.split('_');
    // The 3rd part (index 2) is typically the station code
    if (underscoreParts.length >= 3) {
      const station = underscoreParts[2].toUpperCase();
      // Check if it looks like a valid station code (2-4 letters)
      if (station && /^[A-Z]{2,4}$/.test(station)) {
        return station;
      }
    }
  }

  // Fallback: Extract the first segment before the first dash
  const parts = deviceName.split('-');
  if (parts.length > 0) {
    const station = parts[0].toUpperCase();
    // Return station if it looks valid (not an IP address segment or NCR prefix)
    if (station && !/^\d+$/.test(station) && !station.startsWith('NCR')) {
      return station;
    }
  }

  return 'UNKNOWN';
};

/**
 * Parse the JSON data field from event log
 */
export const parseEventLogData = (dataString: string): EventLogData | null => {
  try {
    return JSON.parse(dataString) as EventLogData;
  } catch {
    return null;
  }
};

/**
 * Convert API port value to PortStatus
 * API uses "0" for DOWN, "1" for UP
 */
export const convertPortStatus = (value: string): PortStatus => {
  return value === '1' ? 'UP' : 'DOWN';
};

/**
 * Convert speed value to readable format
 * API returns "0", "1", etc. which may represent speed settings
 */
export const convertPortSpeed = (value: string): string => {
  // Assuming speed values: 0 = 10Mbps, 1 = 100Mbps, 2 = 1000Mbps
  switch (value) {
    case '0':
      return '10Mbps';
    case '1':
      return '100Mbps';
    case '2':
      return '1000Mbps';
    default:
      return '100Mbps';
  }
};

/**
 * Format ISO timestamp to display format in IST (Indian Standard Time)
 */
export const formatTimestamp = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    // Format in IST timezone using toLocaleString
    const istString = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    });
    // Returns format like "06/01/2026, 14:30:45" - remove the comma
    return istString.replace(',', '');
  } catch {
    return isoString;
  }
};

/**
 * Get relative time string for alerts
 */
export const getRelativeTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } catch {
    return 'Unknown';
  }
};

/**
 * Calculate remaining life and installation age from dates
 */
const calculateLifeInfo = (installationDate: string, codalLife: string): { remainingLife: string; installationAge: string } => {
  try {
    if (!installationDate || installationDate === 'N/A') {
      return { remainingLife: 'N/A', installationAge: 'N/A' };
    }

    const installDate = new Date(installationDate);
    const now = new Date();
    const ageMs = now.getTime() - installDate.getTime();
    const ageYears = ageMs / (1000 * 60 * 60 * 24 * 365.25);

    // Parse codal life (e.g., "10 Years", "10", "10years")
    const codalYearsMatch = codalLife.match(/(\d+)/);
    const codalYears = codalYearsMatch ? parseInt(codalYearsMatch[1]) : 10;

    const remainingYears = Math.max(0, codalYears - ageYears);

    return {
      remainingLife: `${remainingYears.toFixed(1)} Years`,
      installationAge: `${ageYears.toFixed(1)} Years`,
    };
  } catch {
    return { remainingLife: 'N/A', installationAge: 'N/A' };
  }
};

/**
 * Create asset details for a switch using device asset info from /v2/device API
 */
const createAssetDetails = (
  ipAddress: string,
  deviceName: string,
  deviceAssetInfo?: KavachDeviceAssetInfo | null
): AssetDetails => {
  // If we have device asset info from API, use it
  if (deviceAssetInfo) {
    const lifeInfo = calculateLifeInfo(deviceAssetInfo.installationDate, deviceAssetInfo.codalLife || '10 Years');

    // Calculate warranty status
    let warrantyStatus: 'Active' | 'Expired' = 'Active';
    if (deviceAssetInfo.warrantyExpiry) {
      try {
        const warrantyDate = new Date(deviceAssetInfo.warrantyExpiry);
        warrantyStatus = warrantyDate > new Date() ? 'Active' : 'Expired';
      } catch {
        // Keep default 'Active'
      }
    }

    return {
      // From Device API
      make: deviceAssetInfo.make || 'N/A',
      model: deviceAssetInfo.model || 'N/A',
      serialNumber: deviceAssetInfo.serialNumber || 'N/A',
      manufactureNumber: deviceAssetInfo.manufactureNumber || 'N/A',
      purchaseOrderNo: deviceAssetInfo.purchaseOrderNo || 'N/A',
      purchaseDate: deviceAssetInfo.purchaseDate || 'N/A',
      installationDate: deviceAssetInfo.installationDate || 'N/A',
      warrantyExpiry: deviceAssetInfo.warrantyExpiry || 'N/A',
      codalLife: deviceAssetInfo.codalLife || 'N/A',
      firmwareVersion: deviceAssetInfo.firmwareVersion || 'N/A',
      macAddress: deviceAssetInfo.macAddress || 'N/A',
      latitude: deviceAssetInfo.latitude || 'N/A',
      longitude: deviceAssetInfo.longitude || 'N/A',
      assetPosition: deviceAssetInfo.assetPosition || 'N/A',
      platformNo: deviceAssetInfo.platformNo || 'N/A',
      installationTeam: deviceAssetInfo.installationTeam || 'N/A',
      supervisor: deviceAssetInfo.supervisor || 'N/A',
      // From Event Logs API
      ipAddress,
      avgLatency: 'N/A',
      // Network info
      connectedTo: 'N/A',
      uptime: 'N/A',
      packetLoss: 'N/A',
      lastReboot: 'N/A',
      // Maintenance info
      lastMaintenance: 'N/A',
      nextMaintenanceDue: 'N/A',
      // Calculated fields
      remainingLife: lifeInfo.remainingLife,
      installationAge: lifeInfo.installationAge,
      warrantyStatus,
    };
  }

  // Fallback to default values if no device asset info
  return {
    make: 'N/A',
    model: 'N/A',
    serialNumber: 'N/A',
    manufactureNumber: 'N/A',
    purchaseOrderNo: 'N/A',
    purchaseDate: 'N/A',
    installationDate: 'N/A',
    warrantyExpiry: 'N/A',
    codalLife: 'N/A',
    firmwareVersion: 'N/A',
    macAddress: 'N/A',
    latitude: 'N/A',
    longitude: 'N/A',
    assetPosition: 'N/A',
    platformNo: 'N/A',
    installationTeam: 'N/A',
    supervisor: 'N/A',
    ipAddress,
    avgLatency: 'N/A',
    // Network info
    connectedTo: 'N/A',
    uptime: 'N/A',
    packetLoss: 'N/A',
    lastReboot: 'N/A',
    // Maintenance info
    lastMaintenance: 'N/A',
    nextMaintenanceDue: 'N/A',
    // Calculated fields
    remainingLife: 'N/A',
    installationAge: 'N/A',
    warrantyStatus: 'Active',
  };
};

/**
 * Normalize device name for matching with device asset map
 * Event log names like "MNR-DOWN-LINE-ROUTER" should match
 * Device API names like "NCR_PRYJ_MNR_MNR-DOWN-LINE"
 */
const normalizeEventLogName = (name: string): string => {
  if (!name) return '';
  // Convert to uppercase and remove -ROUTER suffix
  return name.toUpperCase().replace(/-ROUTER$/i, '').trim();
};

/**
 * Transform a single EventLog to KavachSwitch
 * Uses device asset info from /v2/device API for actual device name, type, make, model, etc.
 * Matches devices by the clean device name from eventLog.name (e.g., "MXK-UP-LINE")
 * which matches additionaldetail.name from the device API
 */
export const transformEventLogToSwitch = (
  eventLog: EventLogItem,
  parsedData: EventLogData,
  deviceAssetMap?: Map<string, KavachDeviceAssetInfo>
): KavachSwitch => {
  const { results } = parsedData;
  const deviceId = eventLog.deviceId.toString();

  // Use eventLog.name (e.g., "MXK-UP-LINE") which is cleaner than parsedData.deviceName
  // This matches additionaldetail.name from the device API
  const cleanDeviceName = eventLog.name || normalizeEventLogName(parsedData.deviceName);
  const normalizedName = cleanDeviceName.toUpperCase().trim();
  const deviceAssetInfo = deviceAssetMap?.get(normalizedName) || null;

  const ports: { port1: Port; port2: Port; port3: Port; port4: Port } = {
    port1: {
      status: convertPortStatus(results.PORT1_STATUS?.value ?? '0'),
      speed: convertPortSpeed(results.PORT1_SPEED?.value ?? '1'),
    },
    port2: {
      status: convertPortStatus(results.PORT2_STATUS?.value ?? '0'),
      speed: convertPortSpeed(results.PORT2_SPEED?.value ?? '1'),
    },
    port3: {
      status: convertPortStatus(results.PORT3_STATUS?.value ?? '0'),
      speed: convertPortSpeed(results.PORT3_SPEED?.value ?? '1'),
    },
    port4: {
      status: convertPortStatus(results.PORT4_STATUS?.value ?? '0'),
      speed: convertPortSpeed(results.PORT4_SPEED?.value ?? '1'),
    },
  };

  // Use clean device name from event log (e.g., "MXK-UP-LINE")
  // Fallback to device asset name or parsed data name
  const deviceName = cleanDeviceName || deviceAssetInfo?.name || parsedData.deviceName;

  // Use device type from /v2/device API if available
  const deviceType = deviceAssetInfo?.deviceType || 'Media Converter';

  // Use customer name from device asset info, fallback to extracting from device name
  const station = deviceAssetInfo?.customerName || extractStationFromName(deviceName);

  // Use customer ID from device asset info for filtering
  const customerId = deviceAssetInfo?.customerId || '';

  const assetDetails = createAssetDetails(
    parsedData.deviceIp,
    deviceName,
    deviceAssetInfo
  );

  // Add latency from API response
  assetDetails.avgLatency = `${parsedData.totalResponseTimeMs} ms`;

  return {
    id: deviceId,
    name: deviceName,
    station,
    customerId,
    deviceType,
    lastActivity: formatTimestamp(eventLog.createdAt),
    ports,
    assetDetails,
  };
};

/**
 * Aggregate event logs to get latest status per device
 * The API returns multiple events per device, we need the latest one
 */
export const aggregateSwitchData = (
  eventLogs: EventLogItem[]
): Map<number, { eventLog: EventLogItem; parsedData: EventLogData }> => {
  const deviceMap = new Map<number, { eventLog: EventLogItem; parsedData: EventLogData }>();

  for (const eventLog of eventLogs) {
    const parsedData = parseEventLogData(eventLog.data);
    if (!parsedData) continue;

    const existing = deviceMap.get(eventLog.deviceId);
    if (!existing) {
      deviceMap.set(eventLog.deviceId, { eventLog, parsedData });
    } else {
      // Keep the latest event (higher id = more recent)
      if (eventLog.id > existing.eventLog.id) {
        deviceMap.set(eventLog.deviceId, { eventLog, parsedData });
      }
    }
  }

  return deviceMap;
};

/**
 * Generate alerts from switches with port issues
 */
export const generateAlertsFromSwitches = (
  switches: KavachSwitch[]
): KavachAlert[] => {
  const alerts: KavachAlert[] = [];
  let alertId = 1;

  for (const sw of switches) {
    const ports = Object.values(sw.ports);
    const downPorts = ports.filter((p) => p.status === 'DOWN');
    const downCount = downPorts.length;

    if (downCount >= 3) {
      alerts.push({
        id: (alertId++).toString(),
        type: 'critical',
        title: `Critical - ${sw.station} Station`,
        description: `${sw.name} - ${downCount} ports down`,
        timestamp: getRelativeTime(new Date().toISOString()),
        switchId: sw.id,
      });
    } else if (downCount > 0) {
      alerts.push({
        id: (alertId++).toString(),
        type: downCount >= 2 ? 'critical' : 'warning',
        title: `Port Down - ${sw.station} Station`,
        description: `${sw.name} - ${downCount} port${downCount > 1 ? 's' : ''} down`,
        timestamp: getRelativeTime(new Date().toISOString()),
        switchId: sw.id,
      });
    }
  }

  // Sort by type (critical first) and limit to top alerts
  return alerts
    .sort((a, b) => {
      if (a.type === 'critical' && b.type !== 'critical') return -1;
      if (a.type !== 'critical' && b.type === 'critical') return 1;
      return 0;
    })
    .slice(0, 10);
};

/**
 * Calculate statistics from switches
 */
export const calculateStatsFromSwitches = (
  switches: KavachSwitch[],
  alerts: KavachAlert[]
): KavachStats => {
  let portsUp = 0;
  let portsDown = 0;

  switches.forEach((sw) => {
    Object.values(sw.ports).forEach((port) => {
      if (port.status === 'UP') portsUp++;
      else portsDown++;
    });
  });

  const totalPorts = portsUp + portsDown;
  const upPercent = totalPorts > 0 ? (portsUp / totalPorts) * 100 : 0;
  const downPercent = totalPorts > 0 ? (portsDown / totalPorts) * 100 : 0;

  return {
    totalSwitches: switches.length,
    portsUp,
    portsDown,
    activeAlerts: alerts.filter((a) => a.type === 'critical').length,
    upTrend: parseFloat(upPercent.toFixed(1)),
    downTrend: parseFloat((-downPercent).toFixed(1)),
  };
};

/**
 * Calculate station-wise statistics
 */
export const calculateStationStats = (
  switches: KavachSwitch[]
): StationStats[] => {
  const stationMap = new Map<string, { totalSwitches: number; portsUp: number; portsDown: number }>();

  switches.forEach((sw) => {
    const station = sw.station;
    const existing = stationMap.get(station) || { totalSwitches: 0, portsUp: 0, portsDown: 0 };

    existing.totalSwitches++;
    Object.values(sw.ports).forEach((port) => {
      if (port.status === 'UP') existing.portsUp++;
      else existing.portsDown++;
    });

    stationMap.set(station, existing);
  });

  return Array.from(stationMap.entries()).map(([station, stats]) => ({
    station,
    ...stats,
  }));
};

/**
 * Main transformation function for OLD EVENT LOGS API
 * Transforms raw API event logs to Kavach dashboard data
 * @param eventLogs - Raw event logs from API
 * @param totalElements - Total number of elements
 * @param totalPages - Total number of pages
 * @param currentPage - Current page number
 * @param deviceAssetMap - Optional map of device assets from /v2/device API
 */
export const transformEventLogsToKavachData = (
  eventLogs: EventLogItem[],
  totalElements: number,
  totalPages: number,
  currentPage: number,
  deviceAssetMap?: Map<string, KavachDeviceAssetInfo>
): TransformedEventLogsResponse => {
  // Aggregate to get latest status per device
  const deviceMap = aggregateSwitchData(eventLogs);

  // Transform to switches using device asset info
  const switches: KavachSwitch[] = [];
  deviceMap.forEach(({ eventLog, parsedData }) => {
    switches.push(transformEventLogToSwitch(eventLog, parsedData, deviceAssetMap));
  });

  // Sort switches by station and name
  switches.sort((a, b) => {
    if (a.station !== b.station) {
      return a.station.localeCompare(b.station);
    }
    return a.name.localeCompare(b.name);
  });

  // Generate alerts
  const alerts = generateAlertsFromSwitches(switches);

  // Calculate statistics
  const stats = calculateStatsFromSwitches(switches, alerts);

  // Calculate station statistics
  const stationStats = calculateStationStats(switches);

  // Extract unique stations dynamically from switches
  const uniqueStations = [...new Set(switches.map((sw) => sw.station))].sort();
  const stations = ['ALL', ...uniqueStations];

  return {
    switches,
    alerts,
    stats,
    stationStats,
    stations,
    totalElements,
    totalPages,
    currentPage,
  };
};

/**
 * NEW: Helper to get the latest value from a TelemetryValue array
 */
const getLatestValue = <T>(values: TelemetryValue<T>[] | undefined): T | undefined => {
  if (!values || values.length === 0) return undefined;
  // Based on current time-series structure, the last element is the latest
  return values[values.length - 1].value;
};

/**
 * NEW: Helper: Extract port info from new TelemetryResults
 */
const extractPortInfo = (results: any, portPrefix: string) => {
  // Status
  const statusKey = `${portPrefix}_STATUS`;
  const statusData = results?.[statusKey];
  let statusVal = '0';
  if (statusData) {
    if (statusData.value !== undefined) statusVal = String(statusData.value);
    else if (statusData.error) statusVal = '0'; // Timeout/Error -> Down
  }

  // Speed
  const speedKey = `${portPrefix}_SPEED`;
  const speedData = results?.[speedKey];
  let speedVal = '1';
  if (speedData) {
    if (speedData.value !== undefined) speedVal = String(speedData.value);
  }

  return {
    status: convertPortStatus(statusVal),
    speed: convertPortSpeed(speedVal)
  };
};

/**
 * NEW: Transform a single NewTelemetryItem to KavachSwitch
 */
export const transformTelemetryToSwitch = (
  telemetryItem: NewTelemetryItem,
  deviceAssetMap?: Map<string, KavachDeviceAssetInfo>
): KavachSwitch => {
  const deviceId = telemetryItem.deviceId;

  // Normalize name for asset matching
  const getDeviceName = (deviceName: string) => {
    if (deviceName) {
      const [zone, div, stn, device] = deviceName.split("_");
      return device;
    }
    return "";
  }
  const rawDeviceName = getDeviceName(telemetryItem.deviceName);
  const normalizedName = rawDeviceName.toUpperCase().replace(/-ROUTER$/i, '').trim();

  const deviceAssetInfo = deviceAssetMap?.get(normalizedName) || null;

  let telemetryData: any = {};
  // Extract data from dataLists
  if (telemetryItem?.dataLists?.length) {
    telemetryItem?.dataLists.forEach((item) => {
      let dynamicRow: any = {};

      Object.entries(item.data).map(([key, value]) => {
        if (dynamicRow[key]) {
          const oldTs = dynamicRow[key].ts;
          const newTs = value[0].ts;
          if (newTs > oldTs) {
            dynamicRow[key] = {
              ts: newTs,
              value: value[0].value
            };
          }
        } else {
          dynamicRow[key] = {
            ts: value[0].ts,
            value: value[0].value
          };
        }
      });

      telemetryData = { ...telemetryData, ...dynamicRow };
    });
  }
  telemetryData = Object.keys(telemetryData).reduce((acc: any, key) => {
    acc[key] = telemetryData[key].value;
    return acc;
  }, {});

  // Extract fields
  const ipAddress = telemetryData?.deviceIp || '';
  const results = telemetryData?.results || {};

  const ports: { port1: Port; port2: Port; port3: Port; port4: Port } = {
    port1: extractPortInfo(results, 'PORT1'),
    port2: extractPortInfo(results, 'PORT2'),
    port3: extractPortInfo(results, 'PORT3'),
    port4: extractPortInfo(results, 'PORT4'),
  };

  // Device Info
  const deviceName = rawDeviceName || deviceAssetInfo?.name || 'Unknown Device';
  const deviceType = telemetryItem.label || deviceAssetInfo?.deviceType || 'Media Converter';

  // Station
  const station = deviceAssetInfo?.customerName || extractStationFromName(deviceName);
  const customerId = deviceAssetInfo?.customerId || '';

  const assetDetails = createAssetDetails(
    ipAddress,
    deviceName,
    deviceAssetInfo
  );

  // Latency
  const latency = telemetryData?.totalResponseTimeMs;
  if (latency !== undefined) {
    assetDetails.avgLatency = `${latency} ms`;
  }

  // Last Activity
  const lastActivity = telemetryItem.ts ? formatTimestamp(new Date(telemetryItem.ts).toISOString()) : 'N/A';

  return {
    id: deviceId,
    name: deviceName,
    station,
    customerId,
    deviceType,
    lastActivity,
    ports,
    assetDetails,
  };
};

/**
 * NEW: Main transformation function for NEW TELEMETRY API
 * Transforms telemetry items to Kavach dashboard data
 */
export const transformTelemetryToKavachData = (
  items: NewTelemetryItem[],
  totalElements: number,
  totalPages: number,
  currentPage: number,
  deviceAssetMap?: Map<string, KavachDeviceAssetInfo>
): TransformedEventLogsResponse => {
  const switches: KavachSwitch[] = items.map(item =>
    transformTelemetryToSwitch(item, deviceAssetMap)
  );

  // Sort switches by station and name
  switches.sort((a, b) => {
    if (a.station !== b.station) {
      return a.station.localeCompare(b.station);
    }
    return a.name.localeCompare(b.name);
  });

  const alerts = generateAlertsFromSwitches(switches);
  const stats = calculateStatsFromSwitches(switches, alerts);
  const stationStats = calculateStationStats(switches);

  const uniqueStations = [...new Set(switches.map((sw) => sw.station))].sort();
  const stations = ['ALL', ...uniqueStations];

  return {
    switches,
    alerts,
    stats,
    stationStats,
    stations,
    totalElements,
    totalPages,
    currentPage,
  };
};
